import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

export class PostcodeCheck extends Component {
  requiredRefs = ['form', 'input', 'submit', 'result', 'rates'];

  static RATE_QUERY = `
    mutation DeliveryRates($input: CartInput!) {
      cartCreate(input: $input) {
        cart {
          deliveryGroups(first: 10) {
            nodes {
              deliveryOptions {
                handle
                title
                description
                estimatedCost {
                  amount
                  currencyCode
                }
              }
            }
          }
        }
        userErrors {
          field
          message
        }
        warnings {
          code
          message
        }
      }
    }
  `;

  #section;
  #abortController;
  #requestSequence = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#section = this.closest('.shopify-section');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.#syncSubmitState();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.#section = undefined;
    super.disconnectedCallback();
  }

  static isValidPostcode(value) {
    return /^\d{4}$/.test(value);
  }

  static variantGid(value) {
    return value.startsWith('gid://') ? value : `gid://shopify/ProductVariant/${value}`;
  }

  static buildInput(variantId, postcode, countryCode) {
    return {
      lines: [{ merchandiseId: PostcodeCheck.variantGid(variantId), quantity: 1 }],
      buyerIdentity: { countryCode },
      delivery: {
        addresses: [
          {
            selected: true,
            oneTimeUse: true,
            validationStrategy: 'COUNTRY_CODE_ONLY',
            address: { deliveryAddress: { countryCode, zip: postcode } },
          },
        ],
      },
    };
  }

  static normaliseRates(groups) {
    const rates = [];
    const seen = new Set();

    for (const group of groups ?? []) {
      for (const option of group?.deliveryOptions ?? []) {
        const title = option?.title ?? '';
        const amount = option?.estimatedCost?.amount ?? '';
        const currencyCode = option?.estimatedCost?.currencyCode ?? '';
        const key = option?.handle ?? `${title}|${amount}|${currencyCode}`;
        if (!title || !amount || !currencyCode || seen.has(key)) continue;

        seen.add(key);
        rates.push({ title, description: option?.description ?? '', amount, currencyCode });
      }
    }

    return rates;
  }

  static hasUnavailableWarning(warnings) {
    return (warnings ?? []).some(({ code }) =>
      ['MERCHANDISE_OUT_OF_STOCK', 'MERCHANDISE_NOT_ENOUGH_STOCK'].includes(code),
    );
  }

  static estimateDates(title, ranges, startDate = new Date()) {
    const normalisedTitle = title.trim().toLowerCase();
    const rateType = ['express', 'standard'].find((type) => normalisedTitle.includes(type));
    const [minimumDays, maximumDays] = ranges[rateType] ?? [];

    if (
      !Number.isInteger(minimumDays) ||
      !Number.isInteger(maximumDays) ||
      minimumDays < 1 ||
      maximumDays < minimumDays
    ) {
      return null;
    }

    return [
      PostcodeCheck.addBusinessDays(startDate, minimumDays),
      PostcodeCheck.addBusinessDays(startDate, maximumDays),
    ];
  }

  static addBusinessDays(startDate, days) {
    const date = new Date(startDate);
    let remainingDays = days;

    while (remainingDays > 0) {
      date.setDate(date.getDate() + 1);
      if (date.getDay() !== 0 && date.getDay() !== 6) remainingDays -= 1;
    }

    return date;
  }

  static dateLocale(language, countryCode) {
    try {
      const locale = new Intl.Locale(language || 'en');
      if (locale.region || !countryCode) return locale.toString();
      return `${locale.language}-${countryCode.toUpperCase()}`;
    } catch {
      return 'en-AU';
    }
  }

  static formatMoney(amount, currencyCode) {
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount)) return `${amount} ${currencyCode}`.trim();

    const fractionDigits = Number.isInteger(numericAmount) ? 0 : 2;
    return new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(numericAmount);
  }

  async onSubmit(event) {
    event.preventDefault();

    const postcode = this.refs.input.value.trim();
    if (!PostcodeCheck.isValidPostcode(postcode)) {
      this.#setState('error', this.dataset.invalidMessage ?? '', true);
      return;
    }

    const variantId = this.dataset.variantId ?? '';
    if (!variantId) {
      this.#setState('error', this.dataset.variantMessage ?? '', false);
      return;
    }

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const requestSequence = ++this.#requestSequence;

    this.#setState('loading', this.dataset.loadingMessage ?? '', false);

    try {
      const response = await fetch(this.dataset.apiUrl ?? '/api/2026-07/graphql.json', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        signal: this.#abortController.signal,
        body: JSON.stringify({
          query: PostcodeCheck.RATE_QUERY,
          variables: {
            input: PostcodeCheck.buildInput(variantId, postcode, this.dataset.countryCode ?? 'AU'),
          },
        }),
      });

      if (!response.ok) throw new Error(`Shipping rate request failed with ${response.status}`);

      const payload = await response.json();
      const cartCreate = payload?.data?.cartCreate;
      if (payload?.errors?.length || cartCreate?.userErrors?.length || !cartCreate?.cart) {
        throw new Error('Shipping rate request returned an error');
      }

      if (requestSequence !== this.#requestSequence) return;

      const rates = PostcodeCheck.normaliseRates(cartCreate.cart.deliveryGroups?.nodes);
      if (!rates.length) {
        const isUnavailable = PostcodeCheck.hasUnavailableWarning(cartCreate.warnings);
        const message = isUnavailable ? this.dataset.variantMessage : this.dataset.noRatesMessage;
        this.#setState(isUnavailable ? 'error' : 'empty', message ?? '', false);
        return;
      }

      this.#renderRates(rates);
      const summary = (this.dataset.resultsMessage ?? '').replace('__POSTCODE__', postcode);
      this.#setState('success', summary, false);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      if (requestSequence !== this.#requestSequence) return;
      this.#setState('error', this.dataset.errorMessage ?? '', false);
    } finally {
      if (requestSequence === this.#requestSequence) this.#syncSubmitState();
    }
  }

  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || !event.target.matches('variant-picker')) return;

    this.#abortController?.abort();
    this.#requestSequence += 1;
    this.#clearResult();

    event.promise
      .then(({ detail }) => {
        this.dataset.variantId = detail?.resource?.id ? String(detail.resource.id) : '';
        if (detail?.productId) this.dataset.productId = String(detail.productId);
        this.#syncSubmitState();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') this.#setState('error', this.dataset.errorMessage ?? '', false);
      });
  };

  #renderRates(rates) {
    const elements = rates.map((rate) => {
      const row = document.createElement('p');
      row.className = 'postcode-check__rate';

      const title = document.createElement('span');
      title.className = 'postcode-check__rate-title';
      title.textContent = rate.title;

      const detail = document.createElement('span');
      detail.className = 'postcode-check__rate-detail';
      const price = PostcodeCheck.formatMoney(rate.amount, rate.currencyCode);
      const estimate = this.#formatEstimate(rate);
      const description = estimate || rate.description;
      detail.textContent = description ? `${price} · ${description}` : price;

      row.append(title, document.createElement('br'), detail);
      return row;
    });

    this.refs.rates.replaceChildren(...elements);
    this.refs.rates.hidden = false;
  }

  #formatEstimate(rate) {
    const ranges = {
      standard: [Number(this.dataset.standardMinDays), Number(this.dataset.standardMaxDays)],
      express: [Number(this.dataset.expressMinDays), Number(this.dataset.expressMaxDays)],
    };
    const dates = PostcodeCheck.estimateDates(rate.title, ranges);
    if (!dates) return '';

    const locale = PostcodeCheck.dateLocale(document.documentElement.lang, this.dataset.countryCode);
    const formatter = new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'short',
    });
    const range = formatter.formatRange(...dates);
    return (this.dataset.estimateMessage ?? '').replace('__RANGE__', range);
  }

  #setState(state, message, isInvalid) {
    this.refs.form.setAttribute('aria-busy', String(state === 'loading'));
    this.refs.input.setAttribute('aria-invalid', String(isInvalid));
    this.refs.result.textContent = message;
    this.refs.result.hidden = !message;
    this.refs.result.dataset.state = state;

    if (state !== 'success') {
      this.refs.rates.replaceChildren();
      this.refs.rates.hidden = true;
    }

    this.#syncSubmitState(state === 'loading');
  }

  #clearResult() {
    this.refs.input.setAttribute('aria-invalid', 'false');
    this.refs.result.textContent = '';
    this.refs.result.hidden = true;
    this.refs.result.removeAttribute('data-state');
    this.refs.rates.replaceChildren();
    this.refs.rates.hidden = true;
    this.refs.form.setAttribute('aria-busy', 'false');
  }

  #syncSubmitState(isLoading = false) {
    this.refs.submit.disabled = isLoading || !this.dataset.variantId;
  }
}

if (!customElements.get('postcode-check')) {
  customElements.define('postcode-check', PostcodeCheck);
}
